import PageShell from "../../../design-system/components/PageShell/PageShell";
import Text from "../../../design-system/primitives/components/Text/Text";
import Breadcrumb from "../../../design-system/primitives/components/Breadcrumb/Breadcrumb";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import ProgressIndicator from "../../../design-system/primitives/components/ProgressIndicator/ProgressIndicator";
import { SanityImg } from "../../../lib/images.jsx";
import BlockContent from "../../../design-system/components/BlockContent/BlockContent";
import Button from "../../../design-system/primitives/components/Button/Button";
import type { SanityBlockContent, SparkModule } from "../../../api/spark";
import { StepDuration } from "./components/StepDuration/StepDuration";
import styles from "./SparkStepOverviewWithData.css";

type SparkStepOverviewWithDataProps = {
  step: SparkModule;
  content?: SanityBlockContent | null;
  // All steps of the step's station, or null when it isn't part of one.
  stationSteps?: SparkModule[] | null;
  // Neighbouring steps in the stage; null at either end.
  nextStep?: SparkModule | null;
  prevStep?: SparkModule | null;
  // The place the step sits in, outermost first (phase, stage).
  breadcrumb?: string[];
};

/**
 * A step of a spark: header navigation, the step's content and prev/next
 * buttons.
 */
export const SparkStepOverviewWithData = ({
  step,
  content = null,
  stationSteps = null,
  nextStep = null,
  prevStep = null,
  breadcrumb = [],
}: SparkStepOverviewWithDataProps) => {
  // The first and last steps lead back to the overview.
  const prevStepPath = prevStep ? `../step/${prevStep.slug}` : "..";
  const nextStepPath = nextStep ? `../step/${nextStep.slug}` : "..";

  // Only stations run several steps; a lone step has no progress to show.
  const withProgress = stationSteps && stationSteps.length > 1;
  const stationStepIdx = withProgress
    ? stationSteps.findIndex((s) => s.key === step.key)
    : -1;

  const moduleTitle = step.content.station?.title;

  return (
    // Remount on step change so PageShell's scrolling body starts back at the top.
    <PageShell
      key={step.slug}
      surface="legacy-genially-bg-light"
      titleButton={{
        // The user tour points at this button by id.
        id: "close-button",
        icon: "x",
        ariaLabel: "Zurück zur Übersicht",
        to: "..",
      }}
      titleSlot={
        <div className={styles.headerRow}>
          <div className={styles.titles}>
            <Breadcrumb items={breadcrumb} ariaLabel="Ort im Spark" />
            {moduleTitle && (
              <Text variant="subheader-small">{moduleTitle}</Text>
            )}
          </div>

          <nav
            id="step-navigation"
            className={styles.navigation}
            aria-label="Etappenschrittnavigation"
          >
            {withProgress && (
              <div className={styles.progress}>
                <ProgressIndicator
                  totalSteps={stationSteps.length}
                  currentStep={stationStepIdx + 1}
                  ariaLabel="Fortschritt in der Station"
                />
              </div>
            )}
            <IconButton
              icon="arrow-left"
              ariaLabel={
                prevStep
                  ? "zurück zum vorherigen Schritt"
                  : "Zurück zur Übersicht"
              }
              size="large"
              color="accent"
              variant="secondary"
              to={prevStepPath}
            />
            <IconButton
              icon="arrow-right"
              ariaLabel={
                nextStep
                  ? "weiter zum nächsten Schritt"
                  : "Zurück zur Übersicht"
              }
              size="large"
              color="accent"
              variant="primary"
              to={nextStepPath}
            />
          </nav>
        </div>
      }
    >
      <div className={styles.stepContentContainer}>
        <div className={styles.stepDurationArea}>
          <StepDuration step={step} stationSteps={stationSteps} />

          {step.content.typeDarkImage && (
            <SanityImg
              url={step.content.typeDarkImage}
              height={30}
              className={styles.typeDarkImage}
            />
          )}
        </div>
        <h3 className={styles.stepTitle}>{step.title}</h3>
        {content && <BlockContent content={content} />}
        <div className={styles.buttonContainer}>
          <Button
            title="Zurück"
            iconLeft="arrow-left"
            size="large"
            color="accent"
            variant="secondary"
            to={prevStepPath}
          />
          <Button
            title="Weiter"
            iconRight="arrow-right"
            size="large"
            color="accent"
            variant="primary"
            to={nextStepPath}
          />
        </div>
      </div>
    </PageShell>
  );
};
